/* ==========================================================================
   JUVERTO CART BEHAVIOUR
   Owns the live gift-note card, the collapsed row that offers it, the $0.00
   gift note LINE ITEM behind it, the leave animation on a removed row, and
   the reduced-motion override for the drawer itself.

   IT IS NOT ONLY THE CART. The same gift note snippet renders in the product
   page buy box, under Add to Cart. Everything below is written to work from
   any number of instances on a page: every listener is delegated from
   document, mirror() keeps every [data-juv-note-input] carrying one value,
   and syncLine() is the single writer. There is no product-page variant of
   any of this, and there must not be one.

   WHY A LINE ITEM AND NOT A CART ATTRIBUTE. This used to write
   attributes["Gift note"]. That reached the order, but a cart attribute is
   not a line item and Shopify's checkout will not draw it, so the customer
   reached the payment step with no evidence the message existed.

   THE LINE IS A SINGLETON. syncLine() looks the line up by handle first and
   only ever calls cart/add.js when there is none; every later edit is a
   cart/change.js on the key it found. Two Gift Note lines cannot be produced
   by any sequence of edits from any surface.

   THE LINE IS NOT DRAWN AS A CART ROW. It exists in the cart and shows at
   checkout, but the message already appears in the gift note section, so a
   second "Gift Note / Free / <message>" row between the products and the
   totals was a duplicate. There used to be an upsertNoteRow() here that built
   exactly that row; it is gone, and the two cart sections skip the line when
   they render .juv-cart__items. Do not add it back.

   WHERE THE SECTION SITS IS NOT DECIDED HERE. The gift note moved below the
   product lines on 2026-08-25; that order is set in Liquid, in
   sections/cart-drawer.liquid and sections/main-cart.liquid. This file never
   inserts, moves or reorders the section, and an earlier request to move it
   was missed because it was looked for in here.

   ==========================================================================
   form.submit() DOES NOT INCLUDE THE SUBMIT BUTTON. THIS BLOCKED ORDERS.
   ==========================================================================

   REPORTED: on mobile, tapping Checkout reloaded the cart instead of going to
   checkout. The markup was never the problem -- it is
   <button type="submit" name="checkout"> inside <form action="/cart"
   method="POST">, and with no note pending the submit event carried
   submitter = "checkout" and worked.

   The interception below is what broke it. Measured, with a note pending:

     click defaultPrevented   true
     form.submit() called     1
     submit EVENT fired       0        <- form.submit() bypasses it entirely
     payload keys             ["updates[]"]
     payload has "checkout"   FALSE

   A submit button's name/value is contributed by the ACTIVATION, not by the
   form's contents. form.submit() is not an activation, so `checkout` was
   never posted -- and a POST to /cart carrying only updates[] is a cart
   update, which Shopify answers with a redirect back to /cart. Exactly the
   reported symptom, and only when a note was pending, which is exactly when
   the customer has just typed one.

   requestSubmit(button) IS the activation: it fires the submit event and
   contributes the submitter. The hidden-input fallback covers Safari below
   16, which has no requestSubmit.

   ==========================================================================
   THE LINE CAP AND THE CHARACTER CAP ARE TWO DIFFERENT CONSTRAINTS
   ==========================================================================

   MAX_CHARS bounds what can be typed. MAX_LINES bounds what the printed card
   can physically hold, and it is the one that actually protects the card:
   400 characters of ordinary prose is seven lines, but the same 400 in wide
   capitals is more, and a short message carrying typed line breaks can reach
   seven on its own. maxlength alone does not bound the card, so render()
   measures the rendered block and refuses any edit that would begin an
   eighth line.

   THE TWO NUMBERS ARE A PAIR, AND THEY ARE MEASURED. At .juv-note__text's
   3.0cqw on a 210px card (the width at a 390px viewport) the message region
   is 88px and seven lines are 79px; eight would be 90px and overflow. Raising
   MAX_CHARS again, or changing the type size in assets/juverto-cart.css,
   means re-measuring both. The measurement table is in that file next to
   .juv-note__text.

   THERE IS NO AUTO-SHRINK. An earlier version scaled the type down to make
   long messages fit, which made the preview lie about the print.

   NO requestAnimationFrame ANYWHERE IN HERE. The preview environment this is
   verified in runs with visibilityState "hidden", where rAF never fires.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__juvertoCart) return;
  window.__juvertoCart = true;

  var NOTE_HANDLE = 'juverto-gift-note';
  var LEGACY_ATTR = 'Gift note';

  /* 200 -> 400 on 2026-08-25: 200 ran out mid-sentence on a real message.
     MAX_LINES rose 6 -> 7 with it, and the type size dropped to 3.0cqw to
     make room. See the block above before changing either. */
  var MAX_CHARS = 400;
  var MAX_LINES = 7;
  var SAVE_DELAY = 700;

  var saveTimer = null;
  var writing = false;
  var syncing = false;
  var reapplying = false;

  function reduced() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function allInputs() {
    return document.querySelectorAll('[data-juv-note-input]');
  }

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function postJSON(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  /* ---------------------------------------------------------------------
     THE PREVIEW AND THE COLLAPSED ROW
     --------------------------------------------------------------------- */

  function fieldsFor(input) {
    var rootEl = input.closest('[data-juv-note]');
    if (!rootEl) return null;
    return {
      root: rootEl,
      text: rootEl.querySelector('[data-juv-note-text]'),
      ghost: rootEl.querySelector('[data-juv-note-ghost]'),
      counter: rootEl.querySelector('[data-juv-note-counter]'),
      message: rootEl.querySelector('[data-juv-note-message]'),
      summaryMsg: rootEl.querySelector('[data-juv-note-summary-msg]'),
      action: rootEl.querySelector('[data-juv-note-action]')
    };
  }

  function lineCount(parts) {
    if (!parts.text || !parts.message) return 0;
    /* Zero while the <details> is closed, which disables the line guard --
       correct, because nothing can be typed into a closed panel, and mirror()
       repaints programmatically anyway. */
    if (!parts.message.clientWidth) return 0;
    var lh = parseFloat(window.getComputedStyle(parts.text).lineHeight);
    if (!lh || isNaN(lh)) return 0;
    return Math.round(parts.text.scrollHeight / lh);
  }

  function paint(input) {
    var parts = fieldsFor(input);
    if (!parts) return null;

    var value = String(input.value || '');
    if (value.length > MAX_CHARS) {
      value = value.slice(0, MAX_CHARS);
      input.value = value;
    }

    var empty = value.trim() === '';

    /* EVERY WRITE BELOW IS GUARDED: nothing is written unless it changes.

       This is not tidiness. observe() watches the whole <body> for mutations
       and repaints on each batch, and a repaint that rewrites the same text
       and the same attributes IS a batch of mutations -- the observer fired
       again 40ms later, forever. Measured 2026-09-13: 340 DOM mutations in
       three idle seconds on every product page, a style recalc each time,
       and Lighthouse put it down as seconds of Style & Layout. The `writing`
       flag never caught it because mutation records are delivered after
       paint() has already returned and cleared it. */
    writing = true;
    if (parts.text) {
      setText(parts.text, value);
      setAttr(parts.text, 'data-juv-placeholder', empty ? 'true' : 'false');
    }
    if (parts.ghost && parts.ghost.hidden !== !empty) parts.ghost.hidden = !empty;

    /* THE COLLAPSED ROW. The thumbnail never changes -- it is the invitation
       and it is present in every state. What swaps is the label: the Cinzel
       "Add a gift note" gives way to the message itself, and Free gives way
       to Edit. Newlines collapse to spaces because the row is ONE line that
       ellipsises; a typed line break would otherwise blow the row's height
       out even with white-space: nowrap on some engines. */
    setAttr(parts.root, 'data-juv-has-note', empty ? 'false' : 'true');
    if (parts.summaryMsg) {
      setText(parts.summaryMsg, value.replace(/\s+/g, ' ').trim());
    }
    /* THE TWO LABELS ARE NOT HARDCODED ANY MORE. They are theme settings
       (Theme settings > Gift note), written onto the root by
       snippets/juverto-gift-note.liquid as data attributes, because this file
       is a static asset and cannot read Liquid. The defaults below only apply
       if the markup predates that change. */
    if (parts.action) {
      var freeLabel = parts.root.getAttribute('data-juv-free-label') || 'Free';
      var editLabel = parts.root.getAttribute('data-juv-edit-label') || 'Edit';
      setText(parts.action, empty ? freeLabel : editLabel);
    }

    if (parts.counter) {
      setText(parts.counter, value.length + ' / ' + MAX_CHARS);
      /* The "nearly full" warning scales with the cap rather than sitting at a
         fixed 20 characters, which was 10% of 200 and would be 5% of 400. */
      setAttr(parts.counter, 'data-juv-near', value.length >= MAX_CHARS - 40 ? 'true' : 'false');
    }
    writing = false;

    return parts;
  }

  function setText(el, value) {
    if (el.textContent !== value) el.textContent = value;
  }

  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function render(input, isProgrammatic) {
    var parts = paint(input);
    if (!parts) return;

    if (isProgrammatic) {
      input.__juvLastGood = input.value;
      return;
    }

    if (lineCount(parts) > MAX_LINES) {
      input.value = typeof input.__juvLastGood === 'string' ? input.__juvLastGood : '';
      paint(input);
      if (parts.counter) {
        parts.counter.setAttribute('data-juv-full', 'true');
        setTimeout(function () { parts.counter.removeAttribute('data-juv-full'); }, 1200);
      }
      return;
    }

    input.__juvLastGood = input.value;
  }

  /* ONE VALUE, EVERY SURFACE. This is what makes the product page field and
     the drawer field two views of the same message rather than two fields.
     Both directions are covered because it runs from a delegated listener on
     document: whichever one the customer types into is the source, and every
     other instance is repainted programmatically (isProgrammatic = true, so
     the line guard does not fight a value it did not cause). */
  function mirror(source) {
    var inputs = allInputs();
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i] === source) continue;
      if (inputs[i].value === source.value) continue;
      inputs[i].value = source.value;
      render(inputs[i], true);
    }
  }

  /* ---------------------------------------------------------------------
     THE CART LINE
     --------------------------------------------------------------------- */

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function noteLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].handle === NOTE_HANDLE) return cart.items[i];
    }
    return null;
  }

  function realItemCount(cart) {
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].handle !== NOTE_HANDLE) n++;
    }
    return n;
  }

  function variantId() {
    var el = document.querySelector('[data-juv-note-variant]');
    if (!el) return null;
    var v = parseInt(el.getAttribute('data-juv-note-variant'), 10);
    return isNaN(v) ? null : v;
  }

  /* THE ONLY WRITER. Every surface funnels through here.

     The lookup-then-branch is what enforces the singleton: cart/add.js is
     reached only when noteLine() found nothing, and every other path is a
     cart/change.js against the key it already has. */
  function syncLine(text) {
    if (syncing) return Promise.resolve();
    syncing = true;

    return getCart().then(function (cart) {
      var line = noteLine(cart);
      var others = realItemCount(cart);
      var vid = variantId();

      var cleanup = cart.attributes && cart.attributes[LEGACY_ATTR]
        ? postJSON('cart/update.js', { attributes: (function () { var a = {}; a[LEGACY_ATTR] = ''; return a; })() })
        : Promise.resolve();

      return cleanup.then(function () {
        if (others === 0) {
          if (line) return postJSON('cart/change.js', { id: line.key, quantity: 0 });
          return null;
        }

        if (text) {
          if (!line) {
            if (!vid) return null;
            return postJSON('cart/add.js', {
              items: [{ id: vid, quantity: 1, properties: { Message: text } }]
            });
          }
          if ((line.properties && line.properties.Message) !== text) {
            return postJSON('cart/change.js', {
              id: line.key,
              quantity: 1,
              properties: { Message: text }
            });
          }
          return null;
        }

        if (line) return postJSON('cart/change.js', { id: line.key, quantity: 0 });
        return null;
      });
    }).catch(function () { /* the next edit retries */ })
      .then(function () { syncing = false; });
  }

  function queueSync(input) {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      saveTimer = null;
      syncLine(String(input.value || '').slice(0, MAX_CHARS).trim());
    }, SAVE_DELAY);
  }

  function flushSync(input) {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    return syncLine(String(input.value || '').slice(0, MAX_CHARS).trim());
  }

  /* ---------------------------------------------------------------------
     A MESSAGE WRITTEN BEFORE THERE IS ANYTHING TO GIVE

     syncLine() deliberately refuses to create the note line while the cart
     holds no real products, and observe() deletes one it finds: a bag
     containing only a free note is an empty bag, and the cart renders its
     empty state for exactly that case.

     That rule is right in the cart and incomplete on the PRODUCT PAGE, where
     the customer now meets the field BEFORE they have added anything. Left
     alone, a message typed first and added second would be silently dropped.

     So the message simply waits in the textarea -- nothing is queued, stored
     or persisted, the field IS the pending state -- and is re-applied the
     moment an add succeeds. By then others >= 1 and the normal path creates
     the line.

     This wraps fetch rather than listening for a cart event because the add
     is performed by the vendor's own product form component and this theme
     publishes no documented event for it. Two guards matter: `reapplying`
     stops the recursion, since syncLine() itself posts to cart/add.js, and
     the reapply is deferred so any in-flight syncLine() has released
     `syncing` before this one asks for it.
     --------------------------------------------------------------------- */

  function pendingText() {
    var inputs = allInputs();
    for (var i = 0; i < inputs.length; i++) {
      var t = String(inputs[i].value || '').slice(0, MAX_CHARS).trim();
      if (t) return t;
    }
    return '';
  }

  (function wrapAddToCart() {
    var native = window.fetch;
    if (typeof native !== 'function') return;

    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* opaque request object; treat as not-an-add */ }

      var isAdd = !reapplying && /\/cart\/add(\.js)?(\?|$)/.test(url);
      var result = native.apply(this, arguments);

      if (!isAdd || !result || typeof result.then !== 'function') return result;

      return result.then(function (response) {
        try {
          if (response && response.ok) {
            var text = pendingText();
            if (text) {
              reapplying = true;
              setTimeout(function () {
                syncLine(text).then(function () { reapplying = false; });
              }, 250);
            }
          }
        } catch (e) { reapplying = false; }
        return response;
      });
    };
  })();

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    render(input, false);
    mirror(input);
    queueSync(input);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    mirror(input);
    flushSync(input);
  });

  document.addEventListener('blur', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    flushSync(input);
  }, true);

  function inputForSubmit(target) {
    var form = target.closest('form');
    if (form) {
      var inside = form.querySelector('[data-juv-note-input]');
      if (inside) return inside;
      var surface = form.closest('.juv-cart-page, cart-drawer');
      if (surface) {
        var near = surface.querySelector('[data-juv-note-input]');
        if (near) return near;
      }
    }
    return document.querySelector('[data-juv-note-input]');
  }

  /* RESUBMIT AS AN ACTIVATION, NOT AS form.submit().

     form.submit() omits the submit button's name/value, so `checkout` never
     reached /cart and Shopify redirected back to the cart. See the block at
     the top of this file -- this cost real orders. */
  function submitAsCheckout(form, button) {
    if (!form) return;

    if (typeof form.requestSubmit === 'function' && button && button.form === form) {
      try {
        form.requestSubmit(button);
        return;
      } catch (e) { /* fall through */ }
    }

    /* Safari below 16 has no requestSubmit. Name the intent explicitly so the
       POST body still carries it. */
    var hidden = form.querySelector('input[type="hidden"][name="checkout"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var trigger = t.closest('[name="checkout"], .juv-cart__checkout, .shopify-payment-button');
    if (!trigger) return;

    var input = inputForSubmit(t);
    if (!input) return;
    if (!saveTimer && !syncing) return;   /* nothing pending: let it through */

    event.preventDefault();
    var form = trigger.closest('form');
    var button = (trigger.tagName === 'BUTTON' && trigger.type === 'submit') ? trigger : null;

    flushSync(input).then(function () {
      submitAsCheckout(form, button);
    });
  }, true);

  /* ---------------------------------------------------------------------
     LEAVING
     --------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    if (reduced()) return;
    var t = event.target;
    if (!t || !t.closest) return;

    var trigger = t.closest('[data-juv-remove]');

    if (!trigger) {
      var step = t.closest('a.quantity-selector__button');
      if (!step) return;
      var href = step.getAttribute('href') || '';
      if (!/[?&]quantity=0(?:&|$)/.test(href)) return;
      trigger = step;
    }

    var item = trigger.closest('.juv-cart__item');
    if (item) item.setAttribute('data-juv-leaving', 'true');
  }, true);

  /* ---------------------------------------------------------------------
     REDUCED MOTION ON THE DRAWER ITSELF
     --------------------------------------------------------------------- */

  function settleDrawer(el) {
    try {
      ['content', 'overlay'].forEach(function (part) {
        var node = el.getShadowPartByName && el.getShadowPartByName(part);
        if (!node || !node.getAnimations) return;
        node.getAnimations().forEach(function (a) {
          try { a.finish(); } catch (e) { /* already finished */ }
        });
      });
    } catch (e) { /* vendor internals changed; motion simply stays on */ }
  }

  function patchDrawerMotion() {
    var Cls = window.customElements && window.customElements.get('cart-drawer');
    if (!Cls || !Cls.prototype || Cls.prototype.__juvPatched) return;

    ['createEnterAnimationControls', 'createLeaveAnimationControls'].forEach(function (name) {
      var original = Cls.prototype[name];
      if (typeof original !== 'function') return;
      Cls.prototype[name] = function () {
        var controls = original.apply(this, arguments);
        if (reduced()) settleDrawer(this);
        return controls;
      };
    });

    Cls.prototype.__juvPatched = true;
  }

  if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined('cart-drawer').then(patchDrawerMotion).catch(function () {});
  }

  /* ---------------------------------------------------------------------
     RE-ARMING
     --------------------------------------------------------------------- */

  var syncTimer = null;

  function syncAll() {
    var inputs = allInputs();
    for (var i = 0; i < inputs.length; i++) render(inputs[i], true);
  }

  function scheduleSync() {
    if (writing) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(syncAll, 40);
  }

  function observe() {
    syncAll();

    getCart().then(function (cart) {
      var line = noteLine(cart);
      if (line && realItemCount(cart) === 0) {
        postJSON('cart/change.js', { id: line.key, quantity: 0 });
      }
    }).catch(function () {});

    try {
      /* Ignore batches that happen entirely inside a note root: those are
         this file's own repaints (see the guard in paint()). A re-render that
         replaces the note wholesale mutates its PARENT, so it still syncs. */
      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var t = records[i].target;
          var el = t.nodeType === 1 ? t : t.parentElement;
          if (!el || !el.closest('[data-juv-note]')) { scheduleSync(); return; }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) { /* sync once and accept it */ }

    window.addEventListener('resize', scheduleSync);

    var imgs = document.querySelectorAll('[data-juv-note-card-img]');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) continue;
      imgs[i].addEventListener('load', scheduleSync, { once: true });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleSync).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
